<script lang="ts">
  /**
   * Reusable round avatar. Renders the student's profile image when available,
   * otherwise a tasteful initials chip with a deterministic per-identity color.
   * Purely presentational — callers fetch + cache the image and pass it in.
   */
  import { initialsFor, hueFor } from "./avatar";

  /** Profile image data URL, or null/undefined to show the initials fallback. */
  export let image: string | null | undefined = null;
  /** Display name or email — drives the initials + fallback color. */
  export let name: string | null | undefined = null;
  /** Square edge length in px. */
  export let size = 48;
  /** Accessible label; defaults to the name/email. */
  export let label: string | null = null;

  $: initials = initialsFor(name);
  $: hue = hueFor(name);
  $: alt = label ?? name ?? "";
</script>

<span
  class="avatar"
  style="--size:{size}px; --hue:{hue};"
  data-testid="avatar"
  data-has-image={image ? "1" : "0"}
  role="img"
  aria-label={alt}
  title={alt}
>
  {#if image}
    <img src={image} alt={alt} />
  {:else}
    <span class="fallback" aria-hidden="true">{initials}</span>
  {/if}
</span>

<style>
  .avatar {
    display: inline-grid;
    place-items: center;
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    overflow: hidden;
    flex: 0 0 auto;
    background: hsl(var(--hue), 45%, 45%);
    color: #fff;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
    user-select: none;
  }
  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .fallback {
    font-weight: 700;
    font-size: calc(var(--size) * 0.42);
    line-height: 1;
    letter-spacing: 0.02em;
  }
</style>
